import { useDialog } from "./Dialogs";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { RunDTO, TaskDTO } from "@csp/shared";
import { isTechnicalPause } from "../lib/pauseReason";
import { api, errorText } from "../lib/api";
import { useLoad } from "../lib/hooks";
import { isJunkFile, uploadName, uploadTaskFile } from "../lib/upload";
import { MissingFieldHelper } from "./MissingFieldHelper";
import { OtpPrompt } from "./OtpPrompt";

export const RESUME_AFTER_HANDLING = "用户确认问题已处理。先重新观察当前页面，检查原暂停原因是否已经解除；已解除则按现有 SOP 从当前步骤继续，未解除则说明具体原因并再次暂停。需要扫码或人工付款时仍暂停等待，不扩大原任务授权。";

export function ExplorationQuestion({ task, question, run, onChanged }: { task: TaskDTO; question?: RunDTO["question"]; run?: RunDTO | null; onChanged: () => void }) {
  const dialog = useDialog();
  const [open, setOpen] = useState(!/^(用户主动打断|自主恢复工具诊断异常)/.test(task.blockReason??""));
  const [answer, setAnswer] = useState("");
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setOpen(!/^(用户主动打断|自主恢复工具诊断异常)/.test(task.blockReason??"")); setAnswer(""); setDiagnosticOpen(false); setCopied(false); }, [task.blockReason]);
  const text = question?.text || task.blockReason || "请说明接下来要做什么";
  const technical = isTechnicalPause(`${text}
${task.blockReason || ""}`);
  const title = technical ? "运行环境 / 工具异常" : "等待你选择下一步";
  const lastStep = run?.steps.at(-1);
  const diagnostic = ["请帮我排查 CSP 自动化运行异常：", `taskId: ${task.id}`, `runId: ${run?.id || "未提供"}`, `执行端: ${run?.executionTarget || "未提供"}`, `发生步骤: ${lastStep ? `${lastStep.k}/${lastStep.n} ${lastStep.label}` : "未记录"}`, `暂停原因: ${task.blockReason || text}`, "请先排查环境或工具 bug，保留当前任务暂停状态；修复后由我决定继续。"].join("\n");
  const copyDiagnostic = async () => {
    setDiagnosticOpen(true); setCopied(false);
    try { await navigator.clipboard.writeText(diagnostic); setCopied(true); } catch { /* 不支持剪贴板时显示可手动复制的文本。 */ }
  };
  const options = question?.options?.length ? question.options : ["Business User", "Individual User"].filter(o => text.includes(o));
  const send = async (instruction = answer.trim()) => {
    setBusy(true); setError(null);
    try { await api.learningAnswer(task.id, instruction, added.map((a) => a.id), readIds); setAdded([]); setReadIds([]); setOpen(false); onChanged(); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  // 缺字段：补文件（选已有类别或新建），继续时把字段和这类文件关联起来
  // 一次性验证码：单独的输入框（老写法把 OTP 写成缺字段的也认出来）
  const OTP_RE = /\bOTP\b|one[- ]time password|verification code|验证码/i;
  const otp = !technical && question ? question.otp ?? ((question.field ? OTP_RE.test(`${question.field.label} ${question.field.zh ?? ""}`) : OTP_RE.test(question.text)) ? { channel: /sms|短信|手机|mobile/i.test(`${question.field?.label ?? ""} ${question.text}`) ? "sms" as const : "email" as const } : null) : null;
  const field = otp ? undefined : question?.field;
  /** 缺字段：用专门的排版（顶部字段 + 标签页） */
  const mf = !technical && !!field;
  const typeQ = useLoad(() => (field ? api.type(task.typeId) : Promise.resolve(null)), [task.typeId, field?.label]);
  const cats = typeQ.data?.manifestSchema.files ?? [];
  const [cat, setCat] = useState("");
  const [newCat, setNewCat] = useState("");
  const [added, setAdded] = useState<{ id: string; name: string }[]>([]);
  /** AI 助理从哪几份已传文件里读（继续时记为字段来源） */
  const [readIds, setReadIds] = useState<string[]>([]);
  const [upMsg, setUpMsg] = useState("");
  useEffect(() => { setAdded([]); setUpMsg(""); setCat(""); setNewCat(""); }, [field?.label]);
  const addFiles = async (list: FileList | null) => {
    const picked = Array.from(list ?? []).filter((f) => !isJunkFile(f));
    if (!picked.length) return;
    setBusy(true); setError(null);
    try {
      let key = cat || "other";
      if (cat === "__new") {
        if (!newCat.trim()) throw new Error("请填写新文件类别的名称，例如「护照资料页」");
        key = (await api.addFileCategory(task.id, newCat.trim())).key;
      }
      for (const [i, f] of picked.entries()) {
        setUpMsg(`上传中（${i + 1}/${picked.length}）${uploadName(f)}`);
        const r = await uploadTaskFile(task.id, key, f);
        setAdded((a) => [...a, { id: r.fileId, name: uploadName(f) }]);
      }
      setUpMsg("已上传，等同步完成后点「补好了，继续」");
      void typeQ.reload();
    } catch (e) { setError(errorText(e)); setUpMsg(""); }
    finally { setBusy(false); }
  };
  // 等银行授权邮件：后端自动读转发到 ss@ 的信并打开链接；申请人也可以自己在手机上授权
  const mailLink = !technical ? question?.mailLink : undefined;
  if (mailLink) return <div className="notice" data-testid="mail-link-wait">
    <p>📧 正在等 <b translate="no">{mailLink.from}</b> 的授权邮件{mailLink.to ? <>（发到 <span translate="no">{mailLink.to}</span>）</> : null}：转发到 ss@kaider.sg 后，系统会自动在执行端浏览器打开授权链接，不用你操作。</p>
    <p className="small muted">也可以让申请人直接用手机扫画面上的二维码，或在自己手机上打开邮件 / 短信里的链接完成授权，然后点下面的按钮。</p>
    <button className="btn" disabled={busy} onClick={() => void send("申请人已在自己手机上完成授权。回读页面状态（不要再点 Confirm 或 Resend），按 SOP 继续。")}>申请人已自己授权，继续</button>
    {error && <p className="err small">{error}</p>}
  </div>;
  return <>
    <div className="row" style={{ gap: 10, alignItems: "center" }}>
      <button className="btn btn-primary btn-big" data-testid="open-question" onClick={() => setOpen(true)}>{technical ? "查看运行异常" : otp ? (otp.channel === "email" ? "📧 验证码（自动读取）" : "🔑 输入验证码") : mf ? "📝 补字段" : "选择下一步"}</button>
      <span className="small muted">{otp ? `政府网站发了验证码到 ${otp.to || (otp.channel === "sms" ? "担保人手机" : "担保人邮箱")}，点按钮输入` : mf ? `缺「${field!.label}」，点按钮补上` : "任务停下来等你决定，点按钮查看"}</span>
    </div>
    {open && createPortal(<div className="modal-mask"><div className={`modal${mf ? " modal-mf" : ""}`} role="dialog" aria-modal="true" aria-label={mf ? `缺字段：${field!.label}` : title}>
      <h2>{otp ? "输入验证码，任务就能继续" : mf ? "补一个字段，任务就能继续" : title}</h2>
      {otp && <OtpPrompt task={task} otp={otp} onDone={() => { setOpen(false); onChanged(); }} onChanged={onChanged} />}
      {technical && <>
        <p className="notice warn">这是运行环境或工具问题，不是业务资料校验失败。任务已暂停，请先与我排查 bug；无需填写下一步业务指引。</p>
        <p>发生步骤：<span translate="no">{lastStep ? `${lastStep.k}/${lastStep.n} ${lastStep.label}` : "未记录"}</span></p>
        <p className="small muted">以下是暂停时的记录，尚未重新检查是否已修复。</p>
      </>}
      {!mf && !otp && <p className="pre" translate="no">{text}</p>}
      {mf && <MissingFieldHelper task={task} field={field!} why={text} where={question?.step ? `第 ${question.step} 步${run?.steps.find((x) => x.k === question.step)?.label ? `「${run!.steps.find((x) => x.k === question.step)!.label}」` : ""}` : null} cats={cats} picked={readIds} onPick={setReadIds} onChanged={onChanged}
        uploadPane={<>
          <div className="small muted">手上有能证明这个字段的文件就传上来；选好类别，以后缺这个字段会提示补这类文件。</div>
          <div className="row">
            <select aria-label="文件类别" value={cat} disabled={busy} onChange={(e) => setCat(e.target.value)} style={{ width: "auto" }}>
              <option value="">其他材料（不分类，不建立关联）</option>
              {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              <option value="__new">＋ 新建文件类别…</option>
            </select>
            {cat === "__new" && <input aria-label="新文件类别名称" placeholder="例如：护照资料页" value={newCat} maxLength={60} disabled={busy} onChange={(e) => setNewCat(e.target.value)} />}
            <label className="btn" style={busy ? { opacity: 0.5, pointerEvents: "none" } : undefined}>选择文件<input type="file" multiple hidden aria-label="补充文件" disabled={busy} onChange={(e) => { void addFiles(e.target.files); e.target.value = ""; }} /></label>
          </div>
          {upMsg && <p className="small">{upMsg}</p>}
          {added.length > 0 && <p className="small">本次补充：{added.map((a) => a.name).join("、")}</p>}
        </>}
        notePane={<label className="field"><span className="lbl">给执行端的话（可选）</span><textarea aria-label="你的指引" maxLength={2000} value={answer} disabled={busy} onChange={e => setAnswer(e.target.value)} placeholder="例如：职业下拉里选 Director" /></label>} />}
      {!technical && !mf && !otp && options.map(o => <label className="check" key={o}><input type="radio" name="next-action" checked={answer === o} onChange={() => setAnswer(o)} disabled={busy} /><span translate="no">{o}</span></label>)}
      {!technical && !mf && !otp && <label className="field"><span className="lbl">你的指引（可选）</span><textarea aria-label="你的指引" maxLength={2000} value={answer} disabled={busy} onChange={e => setAnswer(e.target.value)} /></label>}
      {!technical && !mf && !otp && <p className="small muted">已处理好当前问题可直接继续；需要改变做法时再填写指引。</p>}
      {technical && <>
        <button className="btn btn-primary" onClick={() => void copyDiagnostic()}>复制诊断信息，与我排查 bug</button>
        {diagnosticOpen && <>
          <p>{copied ? "已复制，请粘贴到当前对话中排查；任务保持暂停。" : "请复制下面的诊断信息，粘贴到当前对话中排查。"}</p>
          <textarea aria-label="异常诊断信息" readOnly value={diagnostic} rows={7} onFocus={e => e.target.select()} translate="no" />
        </>}
      </>}
      {error && <p className="notice err">{error}</p>}
      <div className="actions"><button className="btn" disabled={busy} onClick={() => void (async()=>{if(await dialog.confirm({title:"从头重启这个任务？",body:<p>结束当前运行，保留历史和材料，重新从第一步运行。</p>,okText:"从头重启"})){setBusy(true);try{await api.restartTask(task.id);setOpen(false);onChanged();}catch(e){setError(errorText(e));}finally{setBusy(false);}}})()}>从头重启</button><button className="btn" disabled={busy} onClick={() => setOpen(false)}>稍后决定</button>{!otp && <button className="btn" disabled={busy} onClick={() => void send(RESUME_AFTER_HANDLING)}>问题已处理，继续</button>}{!technical && !otp && <button className="btn btn-primary" disabled={busy || (!answer.trim() && !added.length && !field)} onClick={() => void send()}>{added.length ? "补好了，继续" : "按此继续"}</button>}</div>
    </div></div>, document.body)}
  </>;
}
